/* NOTeslint-disable no-unused-vars */
import {createContext, useContext /* , useReducer */, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import useReducerWithCallback from './ReducerWithCallback';
import Util from '../util/Util';
import Analytics from '../util/Analytics';
import {userInfo} from '../util/userInfo';
import CurrentPathUtil from '../util/CurrentPathUtil';
import {smartFetch} from '../util/smartFetch';
import {getAccountId} from '../util/ApiConfig';
import Locale from '../util/Locale';

const ADVERTISING_VIDEO_FOLDER_ID = '___ADV_VIDEO___';
const MEATBALL_ICON_INDEX = 0;
const ADD_ICON_INDEX = 1;
const MOVE_ICON_INDEX = 2;
const CLOSE_MOVING_INDEX = 3;
const EDIT_ICON_INDEX = 4;
const DELETE_ICON_INDEX = 5;

const NavTreeContext = createContext();

let workingNavTree = null;

/**
 * extractAdvData removes a child JSON object with the description matching the ADVERTISING_VIDEO_FOLDER_ID const returning the child object
 * @param children Array of JSON objects representing child folders
 * @returns JSON object The removed json child that matched ADVERTISING_VIDEO_FOLDER_ID description or null if child is not found.
 */
function extractAdvData(children) {
    if (children) {
        let advIdx = null;
        for (let i = 0; i < children.length; i += 1) {
            if (children[i].description === ADVERTISING_VIDEO_FOLDER_ID) {
                advIdx = i;
                break;
            }
        }

        if (advIdx !== null) {
            return children.splice(advIdx, 1)[0];
        }
        return null;
    }
    return null;
}

function processFolderData(payload, topNode) {
    const node = {};
    if ('description' in payload) {
        node.label = payload.description;
    }
    if ('value' in payload) {
        node.path = payload.value;
    }
    if ('config' in payload) {
        node.config = payload.config;
    }
    if (payload.locked === true) {
        node.icons = ['readonly:lock'];
        node.locked = true;
    } else if (!payload.config) {
        node.icons = [MEATBALL_ICON_INDEX]; // Take the 1st defined icon (meatballs)
    }

    if ('children' in payload && payload.children !== null) {
        node.disclosure = false;
        node.children = [];
        for (let i = 0; i < payload.children.length; i += 1) {
            node.children.push(processFolderData(payload.children[i], topNode));
        }
        if (node.children.length === 0) {
            node.children.push({loading: true}); // Setup for lazy loading of children
        }
    }
    return node;
}

// This *will* change rawData due to extractAdvData decision to extract potential advertising data from it
// Assumption: current rawData has no further use for this app after we process it here
//
function createFoldersFromRawData(rawData, sources) {
    let folderData = null;
    if (rawData && rawData.description) {
        folderData = {
            count: 0,
            label: Util.ROOT_DIR,
            path: Util.ROOT_DIR,
            locked: false,
            children: []
        };
        const allowWebFolder = sources.includes(Util.SOURCE_TYPE.WEB);

        // Split the adv folder out of the raw data
        const advRawData = extractAdvData(rawData.children);

        const allowAdvFolder = advRawData && sources.includes(Util.SOURCE_TYPE.ADV);
        if (allowWebFolder) {
            const webFolder = processFolderData(rawData, folderData);
            webFolder.config = {verb: 'getFolders', root: 'web', sectionTitle: Locale.getText('WEBSITE')};
            webFolder.disclosure = true;
            webFolder.root = 'default';
            if (allowAdvFolder) {
                webFolder.sectionTitle = Locale.getText('WEBSITE');
            }
            folderData.children.push(webFolder);
        }

        if (allowAdvFolder) {
            const advFolder = processFolderData(advRawData, folderData);
            advFolder.label = Locale.getText('VIDEO');
            delete advFolder.disclosure;
            delete advFolder.icons;
            advFolder.config = {
                verb: 'getFolders',
                root: 'advertising',
                sectionTitle: Locale.getText('ADVERTISING')
            };
            advFolder.root = advFolder.config.root;
            if (allowWebFolder) {
                advFolder.sectionTitle = advFolder.config.sectionTitle;
            }
            advFolder.preventSubfolder = true;
            folderData.children.push(advFolder);
        }
    }
    return folderData;
}

/**
 * reloadChildren invokes the getChildFolders endpoint, loading the child folders of the target folder sent
 * @param paths Array of libraryId strings starting at _root_ and ending at the libraryId of the folder whose children will be reloaded
 * @returns A reference to the entire updated folderData from the global state
 */
async function reloadChildren(paths) {
    const folderData = JSON.parse(JSON.stringify(workingNavTree.folderData)); // TODO: Still needed? Clone
    const node = Util.findNode(paths, folderData); // Find node in this new cloned tree
    const data = await smartFetch(`${Util.ROOT_PATH}/getChildFolders?libraryId=${node.path}`);

    if (data && 'length' in data && data.length) {
        extractAdvData(data);
        node.children = data.map(childNode => processFolderData(childNode, folderData));
        if (!('disclosure' in node)) {
            node.disclosure = false;
        }
    } else if (node.children && node.children.length) {
        if (node.children[0].loading) {
            delete node.disclosure;
        }
        node.children = [];
    }
    workingNavTree.folderData = folderData;
    return folderData;
}

/*
    This method is more complicated than it might be because of the wrinkle of locked folders.
    Folders like the subfolders of the Manufacturers folder are not loaded by the getFolders API
    so we have to iterate through the savedPaths reloading the children at each step so that we
    end up with the current folder loaded
    */
async function navToSavedPaths() {
    const savedPaths = CurrentPathUtil.loadCurrentPath();
    if (savedPaths && savedPaths.length > 0) {
        let lastFound = Util.findLastNodeOnPaths(savedPaths, workingNavTree.folderData);
        if (lastFound?.paths.length > 1) {
            // exclude the _root_ case which shows up when we are changing accounts, for instance
            while (lastFound && lastFound.paths.length < savedPaths.length) {
                // eslint-disable-next-line no-await-in-loop
                await reloadChildren(lastFound.paths);
                const found = Util.findLastNodeOnPaths(savedPaths, workingNavTree.folderData);
                if (found && found.paths.length > lastFound.paths.length) {
                    lastFound = found;
                } else {
                    lastFound = null;
                }
            }
            if (lastFound && lastFound.paths.length === savedPaths.length) {
                CurrentPathUtil.discloseBreadcrumb(workingNavTree.folderData, lastFound.paths);
                return {paths: lastFound.paths, defaultFolder: lastFound.node};
            }
        }
    }
    // something went wrong, clear the current path so reload will show the root
    CurrentPathUtil.saveCurrentPath(null);
    return null;
}

function getNodeFromSelectedNode() {
    if (workingNavTree.selectedNode) {
        const pathArray =
            workingNavTree.selectedNode.indexOf('>') !== -1
                ? workingNavTree.selectedNode.split('>')
                : [Util.ROOT_DIR, workingNavTree.selectedNode];
        return Util.findNode(pathArray, workingNavTree.folderData);
    }
    return null;
}

function setFolderNodeIcons(node, move = false) {
    if (!node.locked && !node.root) {
        // eslint-disable-next-line no-param-reassign
        node.icons = move
            ? [CLOSE_MOVING_INDEX]
            : [MOVE_ICON_INDEX, ADD_ICON_INDEX, EDIT_ICON_INDEX, DELETE_ICON_INDEX];
    }
}

function getParentPaths(childPaths) {
    return childPaths.slice(0, childPaths.length - 1);
}

function onLocationChange(paths_, move = false) {
    const paths = paths_.slice(0);
    const node = Util.findNode(paths, workingNavTree.folderData);
    let path = Util.nodeLabel(paths, workingNavTree.folderData);
    let splits = path.split(' > '); // Don't display _root_ path
    if (splits.length > 1) {
        splits = splits.slice(1);
        path = splits.join(' > ');
    }

    const folderName = path.lastIndexOf('> ') !== -1 ? path.substr(path.lastIndexOf('> ') + 2) : path;
    // eslint-disable-next-line no-param-reassign
    node.selected = true;
    if (workingNavTree.selectedNode) {
        const selectedNode = getNodeFromSelectedNode();

        if (selectedNode) {
            if (paths.join('>') !== workingNavTree.selectedNode) {
                selectedNode.selected = false;
            }

            if (!selectedNode.locked && paths.join('>') !== workingNavTree.selectedNode && !selectedNode.config) {
                selectedNode.icons = [MEATBALL_ICON_INDEX]; // Take the 1st defined icon (meatballs) to start
            }
        }
    }
    workingNavTree.selectedNode = paths.join('>');
    workingNavTree.locked = node.locked;
    setFolderNodeIcons(node, move);

    workingNavTree.location = path;
    workingNavTree.folderName = folderName;
}

async function loadNavTree(navtree, dispatchNavTree, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };

    const extra = userInfo?.ddc ? `?accountId=${getAccountId()}&userId=${userInfo.ddc.viewingOrActualUser.userId}` : '';
    const data = await smartFetch(`${Util.ROOT_PATH}/getFolders${extra}`);
    const sources = Util.getDataSources();

    const folderData = createFoldersFromRawData(data, sources);
    if (folderData) {
        let defaultFolder = folderData.children[0]; // Load assets from 1st child folder by default
        if (defaultFolder) {
            workingNavTree.folderData = folderData;
            workingNavTree.locked = defaultFolder.locked;
            let paths = [Util.ROOT_DIR, defaultFolder.path];
            const navved = await navToSavedPaths();
            if (navved) {
                defaultFolder = navved.defaultFolder;
                paths = navved.paths;
            }
            onLocationChange(paths);
            dispatchNavTree({type: 'SUCCESS'}, onComplete);
            return true; // Successful
        }
        dispatchNavTree({type: 'ERROR', error: 'NO_DATA_AVAILABLE'}, onComplete);
        return false; // Unsuccessful
    }
    if (data?.message === 'Request Timeout') {
        console.warn('Request Timeout Error Loading Folders: ', data.message); // eslint-disable-line
        dispatchNavTree({type: 'ERROR', error: 'ML3_CONNECTION_ERROR'}, onComplete);
        Analytics.trackEvent('Network Timeout', 'Loading Folders');
    } else {
        console.warn('Network Timeout Error Loading Sidebar: ', {context: 'folders'}); // eslint-disable-line
        dispatchNavTree({type: 'ERROR', error: 'ML3_CONNECTION_ERROR'}, onComplete);
    }
    return false; // Unsuccessful
}

async function selectDisclosure(navtree, dispatchNavTree, paths, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };
    const folderData = JSON.parse(JSON.stringify(workingNavTree.folderData)); // Clone TODO: required still?
    const node = Util.findNode(paths, folderData); // Find node in this new cloned tree

    let disclosureError = false;

    if (!node.disclosure && node.children.length === 1 && (node.children[0].loading || node.children[0].error)) {
        // Make sure to reset the node if it has an error on the last click and
        node.children[0].loading = true;
        node.children[0].error = false;
        // Open the disclosure so that if there is an error down the line we can close it again.
        if (node.disclosure !== undefined) {
            node.disclosure = true; // open
        }
        // Reset the folder data to hide error messaging if there was any in last click
        workingNavTree.folderData = folderData;
        const data = await smartFetch(`${Util.ROOT_PATH}/getChildFolders?libraryId=${node.path}`);

        if (data && 'length' in data && data.length) {
            node.children = data.map(childNode => processFolderData(childNode, folderData));
            node.disclosure = false; // set to false so the toggle will open below
        } else if (data && data.message) {
            // Error loading folder children
            // Network Error Occurs
            Analytics.trackEvent('Network Timeout', 'Loading Folder');
            disclosureError = true;
            // Node is no longer loading, there has been an error
            // error needs to be set to true so that the node will pass the if statement on like 234
            node.children[0].loading = false;
            node.children[0].error = true;
            // If there was an error loading a folders children, make sure to close the disclosure so the user can
            // try again.
            node.disclosure = false;
        } else if (node.children[0].loading) {
            // Loads successfully but has no children
            delete node.disclosure;
        }
    }
    // Toggle disclosure open if closed, if closed, open it
    // UNLESS there was an error opening it
    if (node.disclosure !== undefined && !disclosureError) {
        node.disclosure = !node.disclosure;
    }
    workingNavTree.folderData = folderData;
    dispatchNavTree({type: 'SUCCESS'}, onComplete);
    return folderData;
}

function changeLocation(navtree, dispatchNavTree, paths, move, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };
    onLocationChange(paths, move);
    dispatchNavTree({type: 'SUCCESS'}, onComplete);
}

async function createFolder(navtree, dispatchNavTree, title, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };
    const selectedNode = getNodeFromSelectedNode();
    if (selectedNode && title) {
        const newFolder = title.trim();
        if (newFolder) {
            const parentId = selectedNode.path;
            const payload = JSON.stringify({
                parentId,
                accountId: getAccountId(),
                libraryName: newFolder
            });
            const result = await smartFetch(`${Util.WRITE_ROOT_PATH}/folders`, 'POST', payload);
            const ok = !result || !result?.message;

            if (ok) {
                const parentPaths = workingNavTree.selectedNode.split('>');
                await Util.addDelay(6000);
                await reloadChildren(parentPaths);
                dispatchNavTree({type: 'SUCCESS'}, onComplete);
                return true;
            }
            if (result) {
                const reason = result && result.message ? `because ${result.message}` : '';
                console.warn(`Unable to add folder ${selectedNode.label} ${reason}`); // eslint-disable-line
            }
        }
    }
    dispatchNavTree({type: 'ERROR', error: 'UNABLE_TO_CREATE_FOLDER'}, onComplete);
    return false;
}

async function deleteFolder(navtree, dispatchNavTree, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };
    const selectedNode = getNodeFromSelectedNode();
    let result;
    if (selectedNode) {
        const libraryId = selectedNode.path;
        let reason;

        result = await smartFetch(`${Util.WRITE_ROOT_PATH}/folders/${libraryId}`, 'DELETE');
        const ok = !result || !result?.message;
        if (ok) {
            await Util.addDelay(6000);
            const parentPaths = getParentPaths(workingNavTree.selectedNode.split('>'));
            onLocationChange(parentPaths);
            await reloadChildren(workingNavTree.selectedNode.split('>'));
            dispatchNavTree({type: 'SUCCESS'}, onComplete);
            return true;
        }
        if (result) {
            if (process.env.NODE_ENV !== 'test') {
                reason = result && result.message ? `because ${result.message}` : '';
            }
        }
        console.warn(`Unable to remove folder ${selectedNode.label} ${reason}`); // eslint-disable-line
    }
    dispatchNavTree({type: 'ERROR', error: 'UNABLE_TO_DELETE_FOLDER'}, onComplete);
    return false;
}

async function moveFolder(navtree, dispatchNavTree, srcPaths, dstPaths, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };

    if (srcPaths && dstPaths && srcPaths instanceof Array && dstPaths instanceof Array) {
        const libraryId = srcPaths[srcPaths.length - 1];
        const newParentId = dstPaths[dstPaths.length - 1];
        const result = await smartFetch(
            `${Util.WRITE_ROOT_PATH}/folder/library/${libraryId}/account/${getAccountId()}`,
            'PATCH',
            JSON.stringify({newParentId})
        );
        const ok = result?.status === '200';
        if (ok) {
            const parentOfSrcPaths = getParentPaths(srcPaths);
            await Util.addDelay(6000);
            await reloadChildren(dstPaths); // update tree of new location
            await reloadChildren(parentOfSrcPaths); // update tree of old location
            onLocationChange(dstPaths);
            dispatchNavTree({type: 'SUCCESS'}, onComplete);
            return true;
        }
    }
    dispatchNavTree({type: 'ERROR', error: 'UNABLE_TO_MOVE_FOLDER'}, onComplete);
    return false;
}

async function renameFolder(navtree, dispatchNavTree, rename, onComplete) {
    workingNavTree = {
        folderData: Util.deepCopyObject(navtree.folderData),
        location: navtree.location,
        folderName: navtree.folderName,
        locked: navtree.locked,
        selectedNode: navtree.selectedNode
    };

    const selectedNode = getNodeFromSelectedNode();
    if (selectedNode && rename) {
        const newFolderName = rename.trim();
        if (newFolderName) {
            const oldName = selectedNode.label;
            const libraryId = selectedNode.path;
            const result = await smartFetch(
                `${Util.WRITE_ROOT_PATH}/folder/library/${libraryId}/account/${getAccountId()}`,
                'PATCH',
                JSON.stringify({newName: newFolderName})
            );
            const ok = result?.status === '200';
            if (ok) {
                selectedNode.label = newFolderName;
                const paths = Util.rootedArrayPath(workingNavTree.selectedNode);
                onLocationChange(paths);
                dispatchNavTree({type: 'SUCCESS'}, onComplete);
                return true;
            }
            if (result) {
                const reason = result && result.message ? `because ${result.message}` : '';
                console.warn(`Unable to rename folder from ${oldName} to ${libraryId} ${reason}`); // eslint-disable-line
            }
        }
    }
    dispatchNavTree({type: 'ERROR', error: 'UNABLE_TO_RENAME_FOLDER'}, onComplete);
    return false;
}

function navTreeReducer(navtree, action) {
    switch (action.type) {
        case 'SUCCESS': {
            const newState = {
                ...workingNavTree,
                loading: false,
                error: ''
            };
            workingNavTree = null;
            return newState;
        }
        case 'ERROR': {
            const newState = {
                ...navtree,
                loading: false,
                error: action.error
            };
            workingNavTree = null;
            return newState;
        }
        case 'CANCEL': {
            const newState = {
                ...navtree,
                loading: false,
                error: ''
            };
            workingNavTree = null;
            return newState;
        }
        default:
            return navtree;
    }
}

export default function NavTreeProvider({children}) {
    const [navtree, reduceNavTree] = useReducerWithCallback(navTreeReducer, {
        loading: false,
        error: '',
        folderData: {children: []},
        location: '',
        folderName: '',
        locked: false,
        selectedNode: null
    });

    const dispatchNavTree = useCallback(
        (action, callback) => {
            reduceNavTree(action, callback);
            switch (action.type) {
                case 'INITIALIZE':
                    loadNavTree(navtree, dispatchNavTree, callback);
                    break;
                case 'SELECT_DISCLOSURE':
                    selectDisclosure(navtree, dispatchNavTree, action.paths, callback);
                    break;
                case 'CHANGE_LOCATION':
                    changeLocation(navtree, dispatchNavTree, action.paths, action.move, callback);
                    break;
                case 'CREATE_FOLDER':
                    createFolder(navtree, dispatchNavTree, action.title, callback);
                    break;
                case 'DELETE_FOLDER':
                    deleteFolder(navtree, dispatchNavTree, callback);
                    break;
                case 'MOVE_FOLDER':
                    moveFolder(navtree, dispatchNavTree, action.srcPaths, action.dstPaths, callback);
                    break;
                case 'RENAME_FOLDER':
                    renameFolder(navtree, dispatchNavTree, action.rename, callback);
                    break;
                case 'ERROR':
                    if (process.env.NODE_ENV !== 'test') {
                        console.warn(action.error); // eslint-disable-line
                    }
                    break;
                default:
                    break;
            }
        },
        [navtree] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const value = useMemo(
        () => ({
            navtree,
            dispatchNavTree
        }),
        [navtree, dispatchNavTree]
    );

    return <NavTreeContext.Provider value={value}>{children}</NavTreeContext.Provider>;
}

NavTreeProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired
};

export function useNavTree() {
    const context = useContext(NavTreeContext);
    if (context === undefined) {
        throw new Error('useNavTree must be used within a NavTreeProvider');
    }
    return context;
}
